import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActionArea,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  DialogTitle,
  Box,
  Divider,
  Dialog,
  DialogContent,
  CircularProgress
} from '@mui/material';
import {
  LocalBar,
  SportsBasketball,
  WarningAmber,
  Info as InfoIcon,
  Event as EventIcon,
  MusicNote as MusicNoteIcon
} from '@mui/icons-material';
import { getEvents } from '../../api/api';
import { ReserveStatus } from '../../api/unit/ReserveStatus';
import { Role } from '../../api/unit/Role';
import {
  useOutletContext,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { OutletContextType } from '../root';
import cardImage_b from '../../img/cardImage_b.png';
import cardImage_d from '../../img/cardImage_d.png';
import cardImage_b_d from '../../img/cardImage_b_d.png';

export type TKGEvent = {
  // ID
  eventId: string;
  // イベント名
  eventName: string;
  // イベント日時
  eventDate: string;
  // バスケ開始時間
  bStartTime: Date | null;
  // バスケ終了時間
  bEndTime: Date | null;
  // 飲み会開始時間
  dStartTime: Date | null;
  // 飲み会終了時間
  dEndTime: Date | null;
  // 体育館名
  arenaName: string;
  // 飲み会場
  restaurant: string;
  // バスケ参加費
  bEntryFee: string;
  // 飲み会参加費
  dEntryFee: string;
  // 予約状態区分
  reserveStatus: ReserveStatus;
  // 満員フラグ
  isSoldOut: boolean;
  // 満員表示フラグ
  isSoldOutDisplay: boolean;
};

let user_id = "";

// ============================================================
// 画面部分
// ============================================================
/**
 * 【TKG_RESERVE_0120】
 * @returns ポータル画面
 */
export const EventListScreen = () => {
  const [events, setEvents] = useState<TKGEvent[]>([]);
  const { setUserId, setUserName, setPictureUrl } = useOutletContext<OutletContextType>();
  const [role, setRole] = useState<Role>();
  const [ dialogOpen, setdialogOpen ] = useState<boolean>(false);
  const [ openGoogleCalendar, setOpenGoogleCalendar ] = useState<boolean>(false);

  const navigate = useNavigate();

  // レスポンシブ対応
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // モバイル用のスタイル適用

  // この画面に渡されたパラメータを取得する
  const location = useLocation();
  
  // 初期表示処理
  useEffect(() => {
    // パラメータを取得
    user_id = location.state.user_id;
    const resultList: TKGEvent[] = [];

    if (user_id) {
      getEvents(user_id).then(response => {
        if (response.status === 200) {
          setRole(new Role(response.data.roleCode));
          for (const responseEvent of response.data.tkgReserve0120DetailsObjectLot.tkgReserve0120DetailsObjectList) {
            const event: TKGEvent = {
              eventId: responseEvent.eventId,
              eventName: responseEvent.eventName,
              eventDate: responseEvent.eventDate,
              bStartTime: responseEvent.bStartTime ? new Date(responseEvent.bStartTime) : null,
              bEndTime: responseEvent.bEndTime ? new Date(responseEvent.bEndTime) : null,
              dStartTime: responseEvent.dStartTime ? new Date(responseEvent.dStartTime) : null,
              dEndTime: responseEvent.dEndTime ? new Date(responseEvent.dEndTime) : null,
              arenaName: responseEvent.arenaName,
              restaurant: responseEvent.restaurant,
              bEntryFee: responseEvent.bEntryFee,
              dEntryFee: responseEvent.dEntryFee,
              reserveStatus: new ReserveStatus(responseEvent.reserveStatus),
              isSoldOut: responseEvent.isSoldOut,
              isSoldOutDisplay: false
            }
            // 満員（男女別満員も含む）かつ未予約の場合のみ、満員表示する
            event.isSoldOutDisplay = event.isSoldOut && !(event.reserveStatus?.isReserved());
            resultList.push(event);
          }

          // 昇順にソート(eventDateを辞書順ソート)
          resultList.sort((a, b) => (a.eventDate > b.eventDate ? 1 : -1));

          setEvents(resultList);
        }
      })
    } else {
      setEvents(resultList);
    }
  }, [location.state.user_id])

  /**
   * カードがクリックされたらイベント詳細画面へ遷移する
   */
  const handleEventNavigation = async (eventId: string) => {
    const params = {
      "user_id": user_id,
      "event_id": eventId
    }
    navigate("/event", { state: params });
  };

  // データが存在しない場合は表示データがない旨の文言を表示する
  if (events === null || events.length === 0) {
    return (
      <>
        <Box sx={{ height: '30vh', padding: '10vh', paddingTop: isMobile ? '10vh' : '15vh', paddingBottom: '40vh' }}>
          {role?.hasDevelopmentHigherAuth() ? (
            <Button
              color="inherit"
              variant="contained"
              onClick={() => navigate("/mng/portal", { state: {user_id: user_id} })}
              sx={{
                padding: "12px 24px",
                borderRadius: "8px",
                margin: "16px 0",
                fontSize: "16px",
                width: isMobile ? "70%" : "20%", 
                transition: "all 0.3s ease",
                '&:active': {
                  transform: 'scale(0.98)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                },
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
                }
              }}
            >
              管理者画面へ
            </Button>
          ) : (
            <></>
          )}
          {/* ローディングアイコン */}
          <Box sx={{ height: "20vh", display: 'flex', flexDirection: 'column', alignItems: 'center',  justifyContent: 'center' }}>
            <CircularProgress
              sx={{
                color: '#FF7043',
                size: 60,
                marginBottom: 2,
              }}
            />
          </Box>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
              color: '#fff',
              backgroundColor: '#FF9800',
              padding: '10px',
              borderRadius: '5px',
            }}
          >
            <WarningAmber sx={{ marginRight: '8px' }} /> 
            現在公開されているイベントはございません。<br />ご不明点がある場合は公式LINEまでお問い合わせください！
          </Typography>
          <Button
            color="inherit"
            variant="contained"
            onClick={() => {setUserId("");setUserName("");setPictureUrl("");navigate("/login")}}
            sx={{
              padding: "12px 24px",
              borderRadius: "8px",
              margin: "16px 0",
              fontSize: "16px",
              width: isMobile ? "100%" : "20%", 
              transition: "all 0.3s ease",
              '&:active': {
                transform: 'scale(0.98)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              },
              '&:hover': {
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            TOPへ戻る
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: isMobile ? '10vh' : '15vh',
          flexDirection: 'column',
        }}
      >
        {role?.hasDevelopmentHigherAuth() ? (
          <Button
            color="inherit"
            variant="contained"
            onClick={() => navigate("/mng/portal", { state: {user_id: user_id} })}
            sx={{
              padding: "12px 24px",
              borderRadius: "8px",
              margin: "16px 0",
              fontSize: "16px",
              width: isMobile ? "70%" : "20%", 
              transition: "all 0.3s ease",
              '&:active': {
                transform: 'scale(0.98)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              },
              '&:hover': {
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            管理者画面へ
          </Button>
        ) : (
          <></>
        )}
        {/* 説明を開くボタン */}
        <Button onClick={() => setdialogOpen(!dialogOpen)} variant="outlined" color="primary" sx={{ marginBottom: '20px', borderRadius: '20px' }}>
          <InfoIcon sx={{ marginRight: '2px' }} />この画面の説明
        </Button>
        <Dialog open={dialogOpen} onClose={() => setdialogOpen(!dialogOpen)}   PaperProps={{ sx: { borderRadius: '10px' } }} >
          <DialogTitle>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: '#FB8C00',
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              予約状況のご案内
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.9rem',
                color: '#616161',
                marginBottom: '10px',
                lineHeight: '1.6',
              }}
            >
              予約が完了すると「<strong>確定</strong>」と表示されます。
              <br />
              「支払い確認待ち」や「キャンセル承認待ち」の場合は、管理者側で承認を行ったのち自動的に変化しますのでそのままお待ちください。
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                color: '#FF7043',
                lineHeight: '1.6',
              }}
            >
              その他ご不明点等があれば、公式LINEまでお問合せください。
            </Typography>
          </DialogContent>
        </Dialog>

        {/* カレンダーアイコン */}
        <Box
          onClick={() => setOpenGoogleCalendar(true)}
          sx={{
            borderRadius: "20px",
            backgroundColor: "#FF9800",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            padding: "10px",
            marginBottom: "20px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              boxShadow: "0px 6px 8px rgba(0, 0, 0, 0.3)",
            },
            "&:active": {
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
              transform: "scale(0.95)",
            },
          }}
        >
          <EventIcon sx={{ color: "#FFFFFF", fontSize: 28 }} />
        </Box>

        {/* 曲のリクエストボタン */}
        <a href="https://forms.gle/Zk1vpm49NvbykNoX9" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<MusicNoteIcon />}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              borderRadius: "20px",
              textTransform: "none",
              boxShadow: "3px 3px 10px rgba(0,0,0,0.2)",
            }}
          >
              曲リクエスト
          </Button>
        </a>
      </Box>

      {/* イベントカード */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          '& > :not(style)': {
            m: 1,
            width: 300,
            height: 250,
          },
          alignItems: 'flex-start', 
          height: 'auto',
          textAlign: 'center',
          paddingTop: '5%',
          paddingBottom: '10vh',
          overflowY: 'unset' // 余計なスクロールを無効化
        }}
      >
        {events.map(event => (
          <Box key={event.eventId}>
            <Card
              sx={{
                border: event.isSoldOutDisplay ? '1px solid #A9A9A9' : event.reserveStatus?.getBorderColor() || '1px solid #E0E0E0',
                borderRadius: 3,
                boxShadow: 2,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'scale(1.03)',
                  boxShadow: 5,
                },
                backgroundImage: event.bStartTime ? event.dStartTime ? `url(${cardImage_b_d})` : `url(${cardImage_b})` : `url(${cardImage_d})`, // 背景画像
                backgroundSize: 'cover', // 画像をカード全体にフィットさせる
                backgroundPosition: 'center', // 画像の位置を中央に調整
                backgroundRepeat: 'no-repeat', // 画像を繰り返さない
              }}
            >
              <CardActionArea onClick={() => handleEventNavigation(event.eventId)}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      {/* 予約状態区分とイベント日時 */}
                      <Typography
                        sx={{
                          backgroundColor: event.isSoldOutDisplay ? 'gray' : event.reserveStatus?.getColor() || '#B0BEC5',
                          color: 'white',
                          fontWeight: 'bold',
                          borderRadius: 1,
                          padding: '8px 16px',
                          fontSize: 13,
                          textAlign: 'center',
                          width: '100px'
                        }}
                      >
                        {event?.reserveStatus?.isDesiplayStatus() ? event.reserveStatus?.getName() : (event.isSoldOut ? "満員" : "")}
                      </Typography>
                    </Box>
                  }
                  action={
                    <Typography sx={{ fontSize: 15, fontWeight: 'bold', paddingRight: 2, color: '#424242' }}>
                      {event.eventDate}
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  {/* イベント名 */}
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      mb: 1.5,
                      textAlign: 'center',
                      color: '#424242',
                      letterSpacing: 1,
                      textTransform: 'capitalize',
                    }}
                  >
                    {event.eventName}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-evenly', mb: 2 }}>
                    {/* バスケ時刻 */}
                    {event.bStartTime ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SportsBasketball sx={{ color: '#FF8A65', marginRight: 1 }} />
                        <Typography sx={{ fontSize: 14, color: '#424242' }}>
                          {String(event.bStartTime ? event.bStartTime.getHours() : "").padStart(2, '0')}:
                          {String(event.bStartTime ? event.bStartTime.getMinutes() : "").padStart(2, '0')}- 
                          {String(event.bEndTime ? event.bEndTime.getHours() : "").padStart(2, '0')}:
                          {String(event.bEndTime ? event.bEndTime.getMinutes() : "").padStart(2, '0')}
                        </Typography>
                      </Box>
                    ) : (<></>) }
                    {/* 飲み会時刻 */}
                    {event.dStartTime ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocalBar sx={{ color: '#FF7043', marginRight: 1 }} />
                      <Typography sx={{ fontSize: 14, color: '#424242' }}>
                        {String(event.dStartTime ? event.dStartTime.getHours() : "").padStart(2, '0')}:
                        {String(event.dStartTime ? event.dStartTime.getMinutes() : "").padStart(2, '0')}- 
                        {String(event.dEndTime ? event.dEndTime.getHours() : "").padStart(2, '0')}:
                        {String(event.dEndTime ? event.dEndTime.getMinutes() : "").padStart(2, '0')}
                      </Typography>
                    </Box>
                    ) : (<></>) }
                  </Box>
                  {/* アリーナ名 */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: 14, color: '#424242' }}>
                      {event.arenaName ? event.arenaName : event.restaurant}
                    </Typography>
                  </Box>
                  {/* 参加費 */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: 14, color: '#424242' }}>
                      {event.bEntryFee && event.dEntryFee ? <Box sx={{ display: 'flex', alignItems: 'center' }}><SportsBasketball sx={{ color: '#FF8A65', marginRight: 1 }} />{event.bEntryFee}<LocalBar sx={{ color: '#FF7043', marginRight: 1 }} />{event.dEntryFee}</Box> : <>{event.bEntryFee ? event.bEntryFee : event.dEntryFee}</>}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        ))}
      </Box>

      {/* GoogleCalendarダイアログ */}
      <Dialog open={openGoogleCalendar} onClose={() => setOpenGoogleCalendar(false)}>
        <DialogContent>
          <iframe
            src="https://calendar.google.com/calendar/embed?src=YWVjM2I0YTBiYmQ4ZjVhM2JhNGMxY2JiZjUxYjgyNGQ3ODE3ZWI5NzRmMmQzODdiZGM3MTY2Y2E1YWQwNDcyZEBncm91cC5jYWxlbmRhci5nb29nbGUuY29t&ctz=Asia/Tokyo"
            width="500"
            height="500"
            style={{ borderWidth: 0, maxWidth:"100%" }}
            title="Google Calendar"
          />
        </DialogContent>
      </Dialog>
    </>
  ); 
}
